/**
 * System Prompt Popover Component
 *
 * Shows a (?) icon that reveals the system prompt on click.
 * Used for transparency in LLM mode operations.
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { HelpCircle, Copy, Check, X } from 'lucide-react';

export interface SystemPromptPopoverProps {
  systemPrompt?: string;
  userPrompt?: string;
  className?: string;
  iconSize?: number;
}

interface PopoverPosition {
  top: number;
  left: number;
}

export const SystemPromptPopover: React.FC<SystemPromptPopoverProps> = ({
  systemPrompt,
  userPrompt,
  className = '',
  iconSize = 16,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [copiedSystem, setCopiedSystem] = useState(false);
  const [copiedUser, setCopiedUser] = useState(false);
  const [position, setPosition] = useState<PopoverPosition>({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Calculate position when opening
  const calculatePosition = useCallback(() => {
    if (!triggerRef.current) return;

    const rect = triggerRef.current.getBoundingClientRect();
    const popoverWidth = 384; // w-96 = 24rem = 384px

    // Position below the trigger, but ensure it stays within viewport
    let left = rect.left;
    if (left + popoverWidth > window.innerWidth - 16) {
      left = window.innerWidth - popoverWidth - 16;
    }
    if (left < 16) {
      left = 16;
    }

    setPosition({
      top: rect.bottom + 8,
      left: left,
    });
  }, []);

  // Handle open/close
  const handleToggle = () => {
    if (!isOpen) {
      calculatePosition();
    }
    setIsOpen(!isOpen);
  };

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Close on window scroll (but not popover internal scroll)
  useEffect(() => {
    const handleScroll = (event: Event) => {
      // Don't close if scrolling inside the popover
      if (popoverRef.current?.contains(event.target as Node)) {
        return;
      }
      if (isOpen) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      // Use capture phase to catch scroll events, but check target
      window.addEventListener('scroll', handleScroll, true);
    }

    return () => {
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen]);

  // Reset copy state after delay
  useEffect(() => {
    if (copiedSystem) {
      const timer = setTimeout(() => setCopiedSystem(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [copiedSystem]);

  useEffect(() => {
    if (copiedUser) {
      const timer = setTimeout(() => setCopiedUser(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [copiedUser]);

  const handleCopy = async (text: string, type: 'system' | 'user') => {
    try {
      await navigator.clipboard.writeText(text);
      if (type === 'system') {
        setCopiedSystem(true);
      } else {
        setCopiedUser(true);
      }
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  if (!systemPrompt && !userPrompt) {
    return null;
  }

  return (
    <>
      {/* Trigger Button */}
      <button
        ref={triggerRef}
        type="button"
        onClick={handleToggle}
        className={`text-gray-400 hover:text-primary-600 transition-colors focus:outline-none ${className}`}
        title="查看使用的提示词"
      >
        <HelpCircle size={iconSize} />
      </button>

      {/* Popover - rendered as fixed position portal */}
      {isOpen && (
        <div
          ref={popoverRef}
          className="fixed z-[9999] w-96 max-w-[calc(100vw-32px)] bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden"
          style={{
            top: position.top,
            left: position.left,
          }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-2 bg-gray-50 border-b border-gray-200">
            <span className="text-sm font-medium text-gray-700">使用的提示词</span>
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <X size={16} />
            </button>
          </div>

          {/* Content - scrollable container */}
          <div
            className="overflow-y-auto overscroll-contain"
            style={{ maxHeight: 'min(60vh, 400px)' }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            {/* System Prompt Section */}
            {systemPrompt && (
              <div className="p-4 border-b border-gray-100">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-gray-500 uppercase">System Prompt</span>
                  <button
                    type="button"
                    onClick={() => handleCopy(systemPrompt, 'system')}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                    title="复制"
                  >
                    {copiedSystem ? (
                      <Check size={14} className="text-green-500" />
                    ) : (
                      <Copy size={14} />
                    )}
                  </button>
                </div>
                <pre className="text-xs text-gray-700 whitespace-pre-wrap break-words font-mono bg-gray-50 p-3 rounded-md">
                  {systemPrompt}
                </pre>
              </div>
            )}

            {/* User Prompt Section */}
            {userPrompt && (
              <div className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-gray-500 uppercase">User Prompt</span>
                  <button
                    type="button"
                    onClick={() => handleCopy(userPrompt, 'user')}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                    title="复制"
                  >
                    {copiedUser ? (
                      <Check size={14} className="text-green-500" />
                    ) : (
                      <Copy size={14} />
                    )}
                  </button>
                </div>
                <pre className="text-xs text-gray-700 whitespace-pre-wrap break-words font-mono bg-gray-50 p-3 rounded-md">
                  {userPrompt}
                </pre>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};
